// Inline alert-count badges + status pill, used in list rows. Renders
// nothing for severities with zero count to keep rows quiet.

import type { AlertCounts, SystemEnriched } from "@/types/api";

interface Props {
  enriched: SystemEnriched | undefined;
  loading?: boolean;
}

export function SystemBadges({ enriched, loading }: Props): JSX.Element {
  if (loading || !enriched) {
    return <span className="skeleton inline-block h-5 w-20 align-middle" aria-busy="true" />;
  }
  if (enriched.error) {
    return <span className="pill-medium" title={enriched.error}>err</span>;
  }
  return (
    <span className="inline-flex items-center gap-1.5 align-middle">
      <AlertBadges counts={enriched.alert_counts} />
      {enriched.status && <StatusPill status={enriched.status} />}
      {enriched.stale && <span className="pill-medium" title="Served from stale cache">stale</span>}
    </span>
  );
}

export function AlertBadges({ counts }: { counts: AlertCounts }): JSX.Element {
  const total = counts.high + counts.medium + counts.low;
  if (total === 0) {
    return <span className="pill-ok">all clear</span>;
  }
  return (
    <>
      {counts.high > 0 && (
        <span className="pill-high tabular-nums" title={`${counts.high} HIGH`}>
          {counts.high} H
        </span>
      )}
      {counts.medium > 0 && (
        <span className="pill-medium tabular-nums" title={`${counts.medium} MEDIUM`}>
          {counts.medium} M
        </span>
      )}
      {counts.low > 0 && (
        <span className="pill-low tabular-nums" title={`${counts.low} LOW`}>
          {counts.low} L
        </span>
      )}
    </>
  );
}

export function StatusPill({ status }: { status: string }): JSX.Element {
  const isActive = status.toUpperCase().includes("ACTIVE");
  return <span className={isActive ? "pill-ok" : "pill-medium"}>{status}</span>;
}
